import { Link } from "react-router-dom";
import { Check, CircleDollarSign, Puzzle, ArrowUpRight, Percent, Wrench } from "lucide-react";
import { COMPARISONS, COMPARE_DATE } from "../../seo/publicPages";

/* Ce qui est facturé en supplément chez la plupart des outils du marché, et inclus dans l'abonnement Casanéo. Formulations génériques
   (sans nommer d'éditeur) ; le détail par concurrent est sur les pages /casaneo-vs-… */
const KIND = {
  module: { Icon: Puzzle, label: "Module payant" },
  plan: { Icon: ArrowUpRight, label: "Plan supérieur" },
  commission: { Icon: Percent, label: "Commission" },
  tiers: { Icon: Wrench, label: "Outil tiers à payer" },
  frais: { Icon: CircleDollarSign, label: "Frais facturés" },
};

const ROWS = [
  ["Channel manager 100+ canaux (Airbnb, Booking, Vrbo, Expedia, Google…)", "Inclus, 0 € de surcoût par canal", "module", "Souvent facturé par canal connecté ou par logement en plus de l'abonnement"],
  ["Site de réservation directe", "Inclus, 0 % de commission", "commission", "Module payant, ou commission prélevée sur chaque réservation directe"],
  ["Portail propriétaires, relevés et virements automatiques", "Inclus", "plan", "Réservé aux plans supérieurs ou vendu comme module"],
  ["Planning ménage + application terrain (photos, check-lists)", "Inclus", "tiers", "Application de ménage tierce à abonner séparément"],
  ["Cautions, empreintes bancaires et libération automatique", "Inclus (frais Stripe seuls)", "module", "Protection dommages payante ou prestataire externe"],
  ["Messages automatiques + assistant IA voyageurs (WhatsApp, e-mail)", "Inclus", "module", "IA et WhatsApp vendus en modules séparés"],
  ["Taxe de séjour, comptabilité et exports", "Inclus", "module", "Export comptable ou module finance en supplément"],
  ["Livret d'accueil digital", "Inclus", "tiers", "Outil tiers payant"],
  ["Reprise des données et accompagnement au démarrage", "Inclus", "frais", "Frais d'onboarding ou d'installation facturés"],
  ["Tarification dynamique", "Option JustPrice : 5 €/logement/mois, 1er mois offert", "commission", "Module payant ou pourcentage prélevé sur vos revenus"],
];

export function IncludedTable() {
  return (
    <section id="inclus" className="border-y border-white/5 bg-white/[0.02]" data-testid="landing-included">
      <div className="max-w-6xl mx-auto px-6 sm:px-10 py-20 sm:py-24">
        <p className="text-xs uppercase tracking-[0.2em] text-brass">Un seul prix, tout compris</p>
        <h2 className="display mt-4 text-3xl sm:text-4xl lg:text-5xl text-white leading-tight max-w-3xl">En supplément ailleurs. Inclus chez Casanéo.</h2>
        <p className="mt-3 text-sm text-slate-300 max-w-2xl">Beaucoup d'outils affichent un prix d'appel, puis ajoutent des modules, des plans supérieurs ou une commission par réservation. Chez Casanéo, l'abonnement comprend tout ; la seule option payante est la tarification dynamique JustPrice.</p>

        <div className="mt-8 overflow-x-auto rounded-2xl border border-white/10 bg-[#071e42]/60">
          <table className="w-full text-sm min-w-[760px]">
            <thead>
              <tr className="border-b border-white/10 text-left">
                <th className="px-4 py-3 text-xs uppercase tracking-wider text-slate-400 font-medium w-[38%]">Fonctionnalité</th>
                <th className="px-4 py-3 text-xs uppercase tracking-wider text-brass font-medium w-[26%]">Casanéo</th>
                <th className="px-4 py-3 text-xs uppercase tracking-wider text-slate-400 font-medium">Ailleurs, le plus souvent</th>
              </tr>
            </thead>
            <tbody>
              {ROWS.map(([feature, ours, kind, theirs], i) => {
                const { Icon, label } = KIND[kind];
                return (
                  <tr key={feature} className={`border-b border-white/5 ${i % 2 ? "bg-white/[0.015]" : ""}`} data-testid={`landing-included-row-${i}`}>
                    <td className="px-4 py-3 text-slate-100">{feature}</td>
                    <td className="px-4 py-3"><span className="inline-flex items-start gap-1.5 text-emerald-300 text-xs leading-snug"><Check className="h-4 w-4 shrink-0" /> {ours}</span></td>
                    <td className="px-4 py-3 text-xs text-slate-300 leading-snug"><span className="inline-flex items-center gap-1 rounded-full border border-rose-400/30 bg-rose-500/10 px-2 py-0.5 text-[10px] text-rose-200 mr-2 whitespace-nowrap"><Icon className="h-3 w-3" /> {label}</span>{theirs}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <p className="mt-4 text-[11px] text-slate-500 max-w-3xl" data-testid="landing-included-note">
          Constats généraux relevés sur les sites publics des principaux éditeurs au {COMPARE_DATE} ; chaque outil a sa propre grille, à vérifier avant décision. Détail par éditeur :{" "}
          {COMPARISONS.map((c, i) => <span key={c.slug}>{i > 0 && " · "}<Link to={`/${c.slug}`} className="underline hover:text-slate-300" data-testid={`landing-included-${c.slug}`}>vs {c.name}</Link></span>)}.
        </p>
      </div>
    </section>
  );
}
